import React from "react";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

/**
 * Settings Hub — administrative entry point. Strictly additive.
 * Currently only "Configuración Financiera" is active. More cards will be
 * added in future sprints (shown as disabled placeholders).
 */
export default function SettingsHub({ onOpenFinancial, onOpenProductTypes, onOpenCatalogs, onOpenSmartProducts, onOpenRoles, onOpenPricing, onOpenCompanyInfo, onOpenRbacOfficial }) {
  const { t } = useI18n();

  return (
    <div data-testid={DASHBOARD.settingsHub} className="space-y-8">
      <section>
        <div className="label-overline mb-1">SET / 00</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("settings_title")}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {t("settings_subtitle")}
        </p>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--border)]">
        {/* Configuración Financiera — oculto de la UI (2026-08-11).
            Módulo intacto; cambiar false→true para restaurar. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardFinancial}
          onClick={onOpenFinancial}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-black"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            01 / FINANCIAL
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_financial_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_financial_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Legacy — Tipos de Producto (Sprint 2). Hidden in Sprint 5.2:
            the V2 Master Catalog supersedes this. Endpoints + panel remain
            functional for backward compatibility. Uncomment to restore. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardProductTypes}
          onClick={onOpenProductTypes}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-black"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            02 / PRODUCT TYPES
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_product_types_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_product_types_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Catálogos Maestros — oculto de la UI (2026-08-11).
            Módulo intacto; cambiar false→true para restaurar. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardCatalogs}
          onClick={onOpenCatalogs}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#0E5FFF]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            02 / CATALOGS
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_catalogs_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_catalogs_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Active: V2 — Catálogo de Productos (Smart Products) — oculto de la
            UI (2026-08-11). Módulo intacto; cambiar false→true para restaurar. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardSmartProducts}
          onClick={onOpenSmartProducts}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#FF6B00]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            03 / SMART PRODUCTS
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_smart_products_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_smart_products_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Active: 04 / Roles & Permisos — RBAC v2 (antiguo) — oculto de la
            UI (2026-08-11). Módulo intacto; cambiar false→true para restaurar. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardRoles}
          onClick={onOpenRoles}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#7C3AED]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            04 / RBAC
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_roles_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_roles_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Active: 05 / Motor de Precios — oculto de la UI (2026-08-11).
            Módulo intacto; cambiar false→true para restaurar. */}
        {false && (
        <button
          data-testid={DASHBOARD.settingsCardPricing}
          onClick={onOpenPricing}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#059669]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            05 / PRICING
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            {t("settings_pricing_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_pricing_card_desc")}
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>
        )}

        {/* Active: 07 / Company Info (Phase D1.1) */}
        <button
          data-testid="settings-card-company-info"
          onClick={onOpenCompanyInfo}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#E5007D]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            07 / COMPANY INFO
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            Información de la Empresa
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            Logo, oficinas (Murfreesboro / La Vergne), footer de cotización y métodos de pago.
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>

        {/* Active: 08 / RBAC Oficial (G4) — Interfaz oficial de Roles y Permisos */}
        <button
          data-testid="settings-card-rbac-official"
          onClick={onOpenRbacOfficial}
          className="group relative text-left bg-white p-6 min-h-[180px] hover:bg-[var(--bg)] transition-colors border-l-4 border-l-[#7C3AED]"
        >
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            08 / RBAC OFICIAL · G4
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2">
            Roles y Permisos (Oficial)
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            Interfaz alineada con SECURITY.md v1.0. Roles múltiples, grants, revokes, branch_scope, auditoría y protecciones §10.1 / §10.5.
          </p>
          <div className="absolute bottom-5 right-5 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
            ↗
          </div>
        </button>

        {/* Placeholder: 06 / Automatización — oculto de la UI (2026-08-11).
            Cambiar false→true para restaurar. */}
        {false && (
        <div
          data-testid="settings-card-automation-placeholder"
          aria-disabled="true"
          className="group relative text-left bg-white p-6 min-h-[180px] border-l-4 border-l-[var(--border)] opacity-70 cursor-not-allowed select-none"
        >
          <div className="flex items-center justify-between">
            <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
              06 / AUTOMATION
            </div>
            <span className="font-mono-tech text-[0.6rem] font-bold tracking-[0.2em] px-2 py-1 bg-[var(--bg)] border border-[var(--border)] text-[var(--text-secondary)]">
              {t("settings_coming_soon_badge")}
            </span>
          </div>
          <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-2 text-[var(--text-secondary)]">
            {t("settings_automation_card_title")}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
            {t("settings_automation_card_desc")}
          </p>
        </div>
        )}
      </section>
    </div>
  );
}
